import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { SharePlanType } from '../../types/pool-plans.types';
import { Stakeholder } from '../../types/stakeholder.types';

export const useStakeholdersForExercise = ({
  companyId,
  numberOfFetchedItems = 50,
  enabled = true,
  search,
  planType,
  showTerminated = false,
}: {
  companyId: string;
  enabled?: boolean;
  numberOfFetchedItems?: number;
  planType: SharePlanType;
  search: string;
  showTerminated?: boolean;
}) => {
  const {
    data: stakeholdersData,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useInfiniteQuery<{
    stakeholders: Stakeholder[];
    totalPages: number;
  }>({
    queryKey: [
      QueryKey.GET_STAKEHOLDERS_FOR_EXERCISE,
      {
        search,
        planType,
        showTerminated,
      },
    ],
    queryFn: ({ pageParam = 0 }) => {
      return ApiService.get(
        { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'stakeholders-for-exercise'] },
        {
          queryParams: {
            take: numberOfFetchedItems,
            skip: pageParam,
            search,
            planType,
            showTerminated,
          },
        },
      );
    },
    enabled: !!companyId && enabled,
    initialPageParam: 0,
    refetchOnReconnect: true,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.totalPages > allPages.length ? allPages.length * numberOfFetchedItems : undefined,
  });
  const stakeholders = useMemo(
    () =>
      stakeholdersData?.pages?.reduce<Stakeholder[]>((acc, page) => {
        return [...acc, ...page.stakeholders];
      }, []),
    [stakeholdersData?.pages],
  );

  return {
    stakeholders: stakeholders || [],
    hasNextPage,
    fetchNextPage,
    stakeholdersData,
    isEmpty: stakeholders?.length === 0,
    numberOfFetchedItems,
    isLoading,
  };
};
